/**
 * Typed, sanitized AI error contract (Plan Phase 1b).
 *
 * Every AI failure in the frontend used to arrive as a bare `Error` built by
 * string concatenation at three chokepoints:
 *
 *   - `lib/ai-studio/sse.ts`   threw `new Error(<server message>)`, discarding
 *                              `res.status` and the `errorCode` its own comment
 *                              named.
 *   - `lib/api/client.ts`      retained `statusCode` + `body` but never read
 *                              `errorCode` out of them.
 *   - `lib/editor/ai-request.ts` never read the error body at all.
 *
 * That cost us both ways: the UI could not branch on WHICH failure happened,
 * and the provider's raw text ("gemini-3.1-pro-preview: 429 RESOURCE_EXHAUSTED
 * at generativelanguage.googleapis.com") was pasted straight into a customer-
 * facing banner.
 *
 * The contract:
 *   `parseAiErrorBody(status, body)` — pure extraction. Pulls `errorCode`
 *      (accepting both the REST body key `errorCode` and the SSE frame key
 *      `code`), the HTTP status, and the raw detail. Interprets nothing.
 *   `sanitizeAiError(parsed)`        — SELECTS the user-facing headline from
 *      the fixed tables below. It never composes the headline from server or
 *      provider input, so no upstream string can reach the UI by construction.
 *      The raw detail rides along untouched in `.technical`, for the shared
 *      `TechnicalDetailsDisclosure` (which unmounts its content when collapsed).
 *
 * ── Classification rule (non-negotiable) ────────────────────────────────────
 * Match on `errorCode`, STATUS-AGNOSTIC. The ceilings — the most important
 * cases in the whole vocabulary — arrive as HTTP **429** from
 * `AiStudioQuotaGuard` (`QuotaExceededException`), which is also the status of
 * an ordinary throttle, and with NO status at all on an SSE session row. Only
 * the code tells them apart: until PROD-1623 the guard sent none, and a run that
 * crossed the ceiling part-way read "busy". HTTP status is used ONLY as a
 * last-resort bucket, when the response carried no code or a code we do not
 * know yet. `__tests__/quota-error-code-parity.guard.spec.ts` reads the API's
 * exception so every code it can emit has an entry below.
 */

import { AI_BLOCKER_COPY } from './quota-copy';

/**
 * Known AI error codes → the exact user-facing headline for each.
 *
 * Sourced from the codes the API actually emits (`errorCode:` on hand-thrown
 * exceptions, `code:` on SSE `event: error` frames) across
 * `apps/api/src/modules/ai-studio`, plus the two codes the frontend synthesizes
 * itself (`network`, `unknown`).
 *
 * Rules for anything added here:
 *   - No model name, provider name, endpoint, parameter or status code. Ever.
 *     A guard spec asserts this over the whole table.
 *   - Say what happened and what the operator can do next.
 */
export const AI_ERROR_HEADLINES = {
  // ── Module availability ───────────────────────────────────────────────────
  /**
   * The chat refuses Vishing work on an instance whose voice data must stay
   * in-country (ai-chat.controller, a 403 with `reason: 'voice_residency'`).
   * Like every entry here it needs `aiErrors.<code>` in en, ar, hi and fr —
   * `__tests__/ai-error-headlines-translated.guard.spec.ts` fails without them.
   */
  AI_VISHING_VOICE_RESIDENCY:
    'Vishing chat is unavailable until its in-country chat boundary is verified. Use the Vishing pages for authorised workflows.',
  // ── Credits, quota, throttling ────────────────────────────────────────────
  /** Monthly HF-funded credit ceiling. The guard's 429 — see header. */
  quota_exhausted_use_own_key:
    'The AI credits included with HumanFirewall are used up for this month. Add your own AI provider key in Settings → AI Studio to keep generating.',
  /**
   * The daily output-token ceiling — the same guard, a different clock. It
   * lifts at UTC midnight, so it must never borrow the monthly sentence above.
   * Taken from `AI_BLOCKER_COPY` so the failure and the gate card beside it say
   * the same thing.
   */
  daily_ai_limit_reached: `${AI_BLOCKER_COPY['daily-tokens'].headline} ${AI_BLOCKER_COPY['daily-tokens'].body}`,
  RATE_LIMITED:
    'AI assistance is busy right now. Wait a moment and try again.',

  // ── Provider configuration ────────────────────────────────────────────────
  no_provider_key:
    'No AI provider is configured yet. Add a provider key in Settings → AI Studio to use AI assistance.',
  // A named provider / key failure (StructuredLlmService.terminalFailure,
  // the key resolver, the model-override policy). Each arrives as an HTTP
  // 400 and, unlisted, read "Adjust it and try again" — blaming the prompt
  // for a key or model fault.
  provider_auth:
    'The AI provider key was rejected. An admin can check it in Settings → AI Studio.',
  provider_out_of_credit:
    'The AI provider key has no credit left. An admin can top it up or add another key in Settings → AI Studio.',
  provider_rate_limited:
    'AI assistance is busy right now. Wait a moment and try again.',
  provider_unavailable:
    'The AI provider is unavailable right now. Try again in a moment.',
  provider_unimplemented:
    'The AI provider could not complete this request. Try again; if it keeps failing, an admin can check Settings → AI Studio.',
  provider_unknown:
    'The AI provider could not complete this request. Try again; if it keeps failing, an admin can check Settings → AI Studio.',
  no_tenant_provider_key:
    'This workspace uses its own AI provider key and none is set yet. An admin can add one in Settings → AI Studio.',
  model_override_unavailable:
    'The AI provider settings for this workspace cannot be used. An admin can check them in Settings → AI Studio.',
  model_override_unsupported:
    'The AI provider settings for this workspace cannot be used. An admin can check them in Settings → AI Studio.',
  platform_provider_unsupported:
    'The AI provider settings for this workspace cannot be used. An admin can check them in Settings → AI Studio.',
  byo_provider_unsupported:
    'The AI provider settings for this workspace cannot be used. An admin can check them in Settings → AI Studio.',
  unknown_provider:
    'The AI provider settings for this workspace cannot be used. An admin can check them in Settings → AI Studio.',

  // ── Safety / content refusals ─────────────────────────────────────────────
  leaked_recipient_pii:
    'The generated content included real recipient details, so it was discarded. Try again with a different prompt.',
  llm_refused:
    'AI assistance declined this request. Rephrase the prompt and try again.',

  // ── Image generation ──────────────────────────────────────────────────────
  image_generator_unavailable:
    'AI image generation is unavailable right now. Try again later, or upload an image instead.',
  image_generation_failed:
    'The image could not be generated. Try again with a different description.',
  image_too_large:
    'The generated image was too large to save. Try a simpler description.',

  // ── Generation sessions ───────────────────────────────────────────────────
  generation_session_not_active:
    'This generation session is no longer active. Start a new one to continue.',
  announcement_session_not_active:
    'This generation session is no longer active. Start a new one to continue.',
  announcement_session_invalid:
    'This generation session is not valid. Start a new one to continue.',
  announcement_session_integrity:
    'This generation session could not be verified. Start a new one to continue.',
  SESSION_NOT_FOUND:
    'This generation session no longer exists. Start a new one to continue.',
  SESSION_CANCELLED: 'The generation was cancelled.',
  SESSION_STALLED:
    'The generation stopped before it finished. Try again.',
  /**
   * NOT a failure of the generation — it finished. The 24h cleanup watchdog
   * (apps/api .../sessions-cleanup.processor.ts) marks a completed run that was
   * never saved to the library. Worded so the operator knows the work
   * succeeded and only needs re-running to capture, because reading this as
   * data loss is what turned it into two High-priority "QR Code failed"
   * tickets on 2026-08-07.
   */
  SESSION_NOT_SAVED:
    'This generation finished but was never saved to the library. Run it again and save it to keep the result.',
  GENERATION_INTERRUPTED:
    'The generation was interrupted before it finished. Try again.',
  AI_STUDIO_SHUTTING_DOWN:
    'AI assistance is restarting. Try again in a moment.',

  // ── Draft / bundle completeness (save-time validation) ────────────────────
  draft_incomplete:
    'This draft is missing pieces that are required before it can be saved. Finish the earlier steps and try again.',
  bundle_incomplete:
    'This draft is missing pieces that are required before it can be saved. Finish the earlier steps and try again.',
  email_not_generated:
    'The email has not been generated yet. Generate it before saving.',
  /**
   * Terminal, like the landing-link refusal below: the session has left
   * 'In Progress', so the missing page can never be written to it
   * (TERMINAL_SAVE_REFUSAL_CODES). The sentence says to generate again.
   */
  landing_page_missing:
    'The awareness landing page was not generated — generate it again.',
  phishing_page_missing:
    'The simulated page was not generated — generate it again.',
  /**
   * Save refused a Link / Data Entry email whose CTA does not carry the
   * landing-link token (apps/api ai-studio.service saveBundle, a 422). Saving
   * the same bundle again cannot succeed, so the sentence says to regenerate.
   */
  email_template_landing_link_required:
    'The generated email has no link to its landing page — generate it again.',
  no_anchor_for_payload_type:
    'There is no reference template for this payload type yet. Pick a different payload type, or add one to the library.',
  fixture_phishing_page_unavailable:
    'The sample content for this request is unavailable.',

  // ── Content Studio (deck generation, translation, card images) ────────────
  // Content Studio spends on three paths and stamps its own codes: the guard's
  // refusal lands on the session row as `quota_exceeded`, the generator's
  // self-rejections as the rest. Without an entry here every one of them fell
  // through to a STATUS bucket — and the session codes arrive over SSE with no
  // status at all, so "a card came back too long", "no generator is
  // registered" and "you are out of credits" all read as the same generic
  // "could not finish this request".
  /** Content Studio's session-level ceiling. Same situation, same sentence. */
  quota_exceeded:
    'The AI credits included with HumanFirewall are used up for this month. Add your own AI provider key in Settings → AI Studio to keep generating.',
  max_tokens_single_card:
    'One card came back longer than a card can hold, so the deck was discarded. Try again, or ask for fewer cards in the brief.',
  image_job_failed:
    'That card image could not be generated. Adjust the image description on the card and try again.',
  no_image_generator:
    'AI image generation is unavailable right now. Try again later, or pick an image from the library instead.',
  image_store_failed:
    'The generated image could not be saved. Try again in a moment.',
  SESSION_GONE:
    'This generation session no longer exists. Start a new one to continue.',
  provider_error:
    'AI assistance could not finish this request. Try again in a moment.',
  // NOTE: Content Studio's fourth session code, `generation_failed`, is NOT
  // listed — `GENERATION_FAILED` below already resolves it. The lookup is
  // case-insensitive (`HEADLINES_BY_LOWER_CODE`), so a second entry differing
  // only in case would SHADOW one of the two rather than add a case. Guarded
  // by lib/content-studio/__tests__/ai-gate.spec.ts.

  // ── Generic upstream generation failures ──────────────────────────────────
  GENERATION_FAILED:
    'AI assistance could not finish this request. Try again in a moment.',
  LLM_ERROR:
    'AI assistance could not finish this request. Try again in a moment.',
  LLM_FAILED:
    'AI assistance could not finish this request. Try again in a moment.',
  CLASSIFY_FAILED:
    'AI assistance could not finish this request. Try again in a moment.',
  provider_bad_json:
    'AI assistance returned a response we could not read. Try again in a moment.',

  // ── Frontend-synthesized ──────────────────────────────────────────────────
  network:
    'Could not reach AI assistance. Check your connection and try again.',
} as const;

export type KnownAiErrorCode = keyof typeof AI_ERROR_HEADLINES;

/**
 * Last-resort buckets, chosen from the HTTP status. Reached ONLY when the
 * response carried no `errorCode`/`code`, or carried one that is not in the
 * table above (a newly-added backend code we have not mapped yet).
 */
export const AI_FALLBACK_HEADLINES = {
  unauthenticated: 'Your session has expired. Sign in again to continue.',
  forbidden: 'You do not have permission to use AI assistance here.',
  not_found: 'That AI request could not be found.',
  invalid_request:
    'That request could not be completed as written. Adjust it and try again.',
  rate_limited: 'AI assistance is busy right now. Wait a moment and try again.',
  timeout: 'AI assistance took too long to respond. Try again.',
  too_large:
    'The request was too large for AI assistance. Shorten it and try again.',
  unavailable:
    'AI assistance is temporarily unavailable. Try again in a moment.',
  unknown: 'AI assistance could not finish this request. Try again in a moment.',
} as const;

export type AiErrorFallback = keyof typeof AI_FALLBACK_HEADLINES;

/**
 * The i18n keys mirror the codes/buckets: `aiErrors.<code>` /
 * `aiErrorFallbacks.<bucket>`. The English sentences above are the fallback rung
 * and the single source the guard pins
 * (`__tests__/ai-error-headlines-translated.guard.spec.ts` asserts the en
 * catalogue equals the table), but a React surface renders the LOCALISED
 * sentence via `resolveAiHeadline(error, t)` — the AI limit headline (PROD-1623)
 * and every other error now speak the admin's language (locale rule
 * 2026-09-27), not English. The headline is still SELECTED by code, never
 * composed from server input, so the "no provider text" property holds for the
 * key exactly as it did for the sentence.
 */

/** Case-insensitive index so `rate_limited` also resolves `RATE_LIMITED`. */
const HEADLINES_BY_LOWER_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(AI_ERROR_HEADLINES).map(([code, headline]) => [
    code.toLowerCase(),
    headline,
  ]),
);

/** The i18n KEY by lowercased code, so a known code resolves its catalogue key. */
const HEADLINE_KEY_BY_LOWER_CODE: Record<string, string> = Object.fromEntries(
  Object.keys(AI_ERROR_HEADLINES).map((code) => [
    code.toLowerCase(),
    `aiErrors.${code}`,
  ]),
);

/**
 * A typed AI failure.
 *
 * `message` is deliberately the sanitized `headline`: existing callers read
 * `err.message` straight into a banner or toast, so anything else would leave
 * the provider leak live everywhere that has not been rewritten yet.
 */
export class AiError extends Error {
  /** Raw server code, verbatim (`errorCode` or SSE `code`). Null if absent. */
  readonly errorCode: string | null;
  /** HTTP status of the failed response. Null for in-stream error frames. */
  readonly httpStatus: number | null;
  /** User-safe text (English), always selected from the tables above. Kept as
   * `message` for non-React callers and as the English fallback rung. */
  readonly headline: string;
  /** The i18n key for `headline`, so a React surface can localise it via
   * `resolveAiHeadline(error, t)`. Always `aiErrors.<code>` /
   * `aiErrorFallbacks.<bucket>` — SELECTED by code, never composed. */
  readonly headlineKey: string;
  /** Raw upstream detail, verbatim — for the collapsed technical disclosure. */
  readonly technical: string;

  constructor(
    errorCode: string | null,
    httpStatus: number | null,
    headline: string,
    technical: string,
    headlineKey: string,
  ) {
    super(headline);
    // `extends Error` + a downlevelled target can break `instanceof`.
    Object.setPrototypeOf(this, AiError.prototype);
    this.name = 'AiError';
    this.errorCode = errorCode;
    this.httpStatus = httpStatus;
    this.headline = headline;
    this.headlineKey = headlineKey;
    this.technical = technical;
  }
}

/**
 * The localised headline for a surface that has a translator. Falls back to the
 * English `headline` via the catalogue's own English rung, so a key missing in a
 * language still reads a sentence, never a raw key.
 */
export function resolveAiHeadline(
  error: AiError,
  t: (key: string) => string,
): string {
  const localised = t(error.headlineKey);
  // `t` returns the key itself when nothing resolves — fall back to English.
  return localised === error.headlineKey ? error.headline : localised;
}

/** The un-interpreted facts pulled off a failed AI response. */
export interface ParsedAiError {
  errorCode: string | null;
  httpStatus: number | null;
  /** Raw detail, verbatim. Never empty — falls back to `HTTP <status>`. */
  technical: string;
}

function firstNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Extract the raw detail. Precedence mirrors `ApiClientError.extractMessage`
 * so the two chokepoints describe a failure identically:
 *   message (string | string[]) → error → issues[].message → errorCode.
 */
function extractTechnical(rec: Record<string, unknown>): string | null {
  const message = rec.message;
  if (Array.isArray(message)) {
    const joined = message.filter((m) => typeof m === 'string').join('. ');
    if (joined.trim().length > 0) return joined;
  } else {
    const single = firstNonEmptyString(message);
    if (single) return single;
  }

  const error = firstNonEmptyString(rec.error);
  if (error) return error;

  if (Array.isArray(rec.issues)) {
    const lines = rec.issues
      .map((i) =>
        i && typeof i === 'object'
          ? firstNonEmptyString((i as Record<string, unknown>).message)
          : null,
      )
      .filter((x): x is string => !!x);
    if (lines.length > 0) return lines.join('. ');
  }

  return (
    firstNonEmptyString(rec.errorCode) ?? firstNonEmptyString(rec.code) ?? null
  );
}

/**
 * Read a failed AI response into the three facts the UI needs. Pure — it
 * classifies nothing and rewrites nothing.
 *
 * @param httpStatus HTTP status, or null for an in-stream SSE `error` frame
 *                   (the response itself was 200).
 * @param body       Parsed JSON body, a plain-text body, or null/undefined.
 */
export function parseAiErrorBody(
  httpStatus: number | null,
  body: unknown,
): ParsedAiError {
  const statusFallback =
    typeof httpStatus === 'number' ? `HTTP ${httpStatus}` : 'Unknown error';

  if (typeof body === 'string') {
    return {
      errorCode: null,
      httpStatus,
      technical: firstNonEmptyString(body) ?? statusFallback,
    };
  }

  if (!body || typeof body !== 'object') {
    return { errorCode: null, httpStatus, technical: statusFallback };
  }

  const rec = body as Record<string, unknown>;
  return {
    // `errorCode` is the REST body key; `code` is the SSE error-frame key
    // (apps/api ai-studio.controller writes `{ message, code }`).
    errorCode:
      firstNonEmptyString(rec.errorCode) ?? firstNonEmptyString(rec.code),
    httpStatus,
    technical: extractTechnical(rec) ?? statusFallback,
  };
}

/** The status bucket name. Only ever consulted when the code did not resolve. */
function fallbackBucketFor(httpStatus: number | null): AiErrorFallback {
  if (httpStatus === null) return 'unknown';
  if (httpStatus === 401) return 'unauthenticated';
  if (httpStatus === 403) return 'forbidden';
  if (httpStatus === 404) return 'not_found';
  if (httpStatus === 408 || httpStatus === 504) return 'timeout';
  if (httpStatus === 413) return 'too_large';
  if (httpStatus === 429) return 'rate_limited';
  if (httpStatus >= 500) return 'unavailable';
  if (httpStatus >= 400) return 'invalid_request';
  return 'unknown';
}

/** Pick the status bucket. Only ever consulted when the code did not resolve. */
function fallbackFor(httpStatus: number | null): string {
  return AI_FALLBACK_HEADLINES[fallbackBucketFor(httpStatus)];
}

/**
 * Turn parsed facts into the typed error the UI renders.
 *
 * The headline is SELECTED — `AI_ERROR_HEADLINES[code]`, else a status bucket.
 * There is no code path that interpolates `technical` into `headline`, which is
 * what makes "no provider text in the UI" a property of the function rather
 * than a thing reviewers have to keep noticing.
 */
export function sanitizeAiError(parsed: ParsedAiError): AiError {
  const { errorCode, httpStatus, technical } = parsed;

  const lower = errorCode?.toLowerCase();
  const byCode = lower ? HEADLINES_BY_LOWER_CODE[lower] : undefined;
  const byCodeKey = lower ? HEADLINE_KEY_BY_LOWER_CODE[lower] : undefined;

  return new AiError(
    errorCode,
    httpStatus,
    byCode ?? fallbackFor(httpStatus),
    technical,
    byCodeKey ?? `aiErrorFallbacks.${fallbackBucketFor(httpStatus)}`,
  );
}

/**
 * Lift anything thrown on an AI path into the contract.
 *
 * Handles the `ApiClientError` that `fetchApi` throws (which already carries
 * `statusCode` + `body`), an `AiError` (returned as-is, so this is idempotent
 * and safe to call at any layer), and any other throw — whose message is kept
 * ONLY in `.technical`, never promoted to the headline.
 *
 * Note: `AbortError` is intentionally NOT special-cased. Callers check
 * `err.name === 'AbortError'` before reaching for this, and an intentional
 * cancel is not a failure to report.
 */
export function toAiError(err: unknown): AiError {
  if (err instanceof AiError) return err;

  if (isApiClientErrorShape(err)) {
    return sanitizeAiError(parseAiErrorBody(err.statusCode, err.body));
  }

  const technical =
    err instanceof Error
      ? firstNonEmptyString(err.message)
      : firstNonEmptyString(String(err));

  return new AiError(
    null,
    null,
    AI_FALLBACK_HEADLINES.unknown,
    technical ?? 'Unknown error',
    'aiErrorFallbacks.unknown',
  );
}

/**
 * Structural check rather than `instanceof ApiClientError` — importing the
 * client into this module would make every AI surface pull in the whole fetch
 * stack, and duplicate class identities across bundles make `instanceof`
 * unreliable anyway.
 */
function isApiClientErrorShape(
  err: unknown,
): err is { statusCode: number; body: unknown } {
  return (
    err instanceof Error &&
    err.name === 'ApiClientError' &&
    typeof (err as { statusCode?: unknown }).statusCode === 'number'
  );
}
